import React, { useMemo, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Paperclip } from 'lucide-react';
import { DataTableColumn } from '../../../types/grid';
import { IndeterminateCheckbox } from '../IndeterminateCheckbox';
import { InlineCellEditor } from '../InlineCellEditor';
import { NestedObjectCell } from '../NestedObjectCell';
import { resolveFileGatewayUrl } from '../../../utils/fileUrl';

interface UseDataTableColumnsProps<T extends Record<string, any>> {
  columns: DataTableColumn<T>[];
  enableSelection?: boolean;
  canEdit?: boolean;
  getRowId: (row: T, index: number) => string;
  activeCell: { rowId: string; colId: string } | null;
  editingCell: { rowId: string; colId: string } | null;
  setEditingCell: (cell: { rowId: string; colId: string } | null) => void;
  onCellCommit?: (rowId: string, colKey: string, newValue: any, oldValue: any) => Promise<void> | void;
}

export function useDataTableColumns<T extends Record<string, any>>({
  columns,
  enableSelection,
  canEdit = false,
  getRowId,
  activeCell,
  editingCell,
  setEditingCell,
  onCellCommit,
}: UseDataTableColumnsProps<T>) {
  // Invariant 13: Helper to ensure each column's minimum width fits its title text + padding + sort icon + filter icon
  const getTitleMinWidth = (
    header: any,
    colId: string,
    isSortable: boolean,
    isFilterable: boolean
  ): number => {
    let titleStr = '';
    if (typeof header === 'string') {
      titleStr = header;
    } else {
      titleStr = colId ? colId.replace(/^data_/, '').replace(/^__/, '') : '';
    }
    const textWidth = Math.ceil((titleStr ? titleStr.length : 6) * 8.5);
    // Base padding & borders (24px) + sort icon (18px) + filter icon (20px) + buffer (12px)
    const overhead = (isSortable ? 18 : 0) + (isFilterable ? 20 : 0) + 36;
    return Math.max(textWidth + overhead, 75);
  };

  const editingCellRef = useRef(editingCell);
  editingCellRef.current = editingCell;
  const activeCellRef = useRef(activeCell);
  activeCellRef.current = activeCell;
  const onCellCommitRef = useRef(onCellCommit);
  onCellCommitRef.current = onCellCommit;

  // Build TanStack Table Column Definitions
  const tanstackColumns = useMemo<ColumnDef<T, any>[]>(() => {
    const list: ColumnDef<T, any>[] = [];

    // Optional Checkbox Selection Column (pinned left)
    if (enableSelection) {
      list.push({
        id: 'select',
        size: 44,
        minSize: 44,
        maxSize: 44,
        enableSorting: false,
        enableHiding: false,
        meta: { isSystem: true, pinned: 'left' },
        header: ({ table }) => (
          <div className="flex items-center justify-center w-full">
            <IndeterminateCheckbox
              checked={table.getIsAllPageRowsSelected()}
              indeterminate={table.getIsSomePageRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
              ariaLabel="Select all rows on page"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-center w-full">
            <IndeterminateCheckbox
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
              ariaLabel={`Select row ${row.index + 1}`}
            />
          </div>
        ),
      });
    }

    // Configured Columns
    columns.forEach((col) => {
      // Invariant 14: Actions column header explicitly "Actions"
      const resolvedHeader =
        col.id === 'actions' && (!col.header || col.header === '') ? 'Actions' : col.header;
      const isSortable = col.sortable !== false;
      const isFilterable =
        col.filterable !== false && !col.isSystem && col.id !== 'select' && col.id !== 'actions';
      const titleMinWidth = getTitleMinWidth(resolvedHeader, col.id, isSortable, isFilterable);
      const minSize = Math.max(col.minWidth || 0, titleMinWidth);
      const size = Math.max(col.width || minSize, minSize);
      const maxSize = Math.max(col.maxWidth || size, size);

      list.push({
        id: col.id,
        header: resolvedHeader,
        accessorFn:
          col.accessorFn ||
          (col.accessorKey ? (row: T) => (row as any)[col.accessorKey!] : (row: T) => (row as any)[col.id]),
        size,
        minSize,
        maxSize,
        enableSorting: col.sortable !== false,
        enableHiding: col.hideable !== false,
        meta: {
          key: col.id,
          dataType: col.dataType || 'text',
          pinned: col.pinned,
          isSystem: col.isSystem,
          editable: col.editable,
          truncate: col.truncate,
        },
        cell: (ctx) => {
          const val = ctx.getValue();
          const rowId = getRowId(ctx.row.original, ctx.row.index);
          const isCellEditing = editingCellRef.current?.rowId === rowId && editingCellRef.current?.colId === col.id;
          const isCellActive = activeCellRef.current?.rowId === rowId && activeCellRef.current?.colId === col.id;

          // Custom column cell renderer takes precedence
          if (col.cell) {
            return col.cell({
              row: ctx.row.original,
              value: val,
              isEditing: isCellEditing,
              isActive: isCellActive,
              onCommit: (newVal) => {
                onCellCommitRef.current?.(rowId, col.id, newVal, val);
                setEditingCell(null);
              },
              rowId,
              columnId: col.id,
            });
          }

          if (isCellEditing && canEdit && col.editable !== false) {
            return (
              <InlineCellEditor
                initialValue={val}
                dataType={col.dataType || 'text'}
                onCommit={(newVal) => {
                  onCellCommitRef.current?.(rowId, col.id, newVal, val);
                  setEditingCell(null);
                }}
                onCancel={() => setEditingCell(null)}
              />
            );
          }

          // Default smart data-type formatters
          if (val === null || val === undefined) {
            return <span className="text-cf-textMuted">—</span>;
          }

          if (col.dataType === 'boolean') {
            const isTrue = val === true || val === 'true' || val === 1 || val === '1';
            return (
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold whitespace-nowrap ${
                  isTrue
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                    : 'bg-cf-surfaceHover text-cf-textMuted border border-cf-border'
                }`}
              >
                {isTrue ? 'TRUE' : 'FALSE'}
              </span>
            );
          }

          if (col.dataType === 'status') {
            const str = String(val).toUpperCase();
            const isSuccess = ['OK', 'ACTIVE', 'DELIVERED', 'TRUE', 'PASS'].includes(str);
            const isDanger = ['SPAM', 'FAILED', 'ERROR', 'QUARANTINED', 'TRAPPED'].includes(str);
            return (
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    isSuccess
                      ? 'bg-emerald-500 dark:bg-emerald-400'
                      : isDanger
                      ? 'bg-rose-500 dark:bg-rose-400'
                      : 'bg-amber-500 dark:bg-amber-400'
                  }`}
                />
                <span className="text-xs font-medium text-cf-textPrimary">
                  {str}
                </span>
              </div>
            );
          }

          if (col.dataType === 'number') {
            const n = Number(val);
            return (
              <span className="tabular-nums text-cf-textPrimary whitespace-nowrap font-sans text-xs">
                {!isNaN(n) ? (Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2)) : String(val)}
              </span>
            );
          }

          if (col.dataType === 'date') {
            try {
              const d = new Date(val);
              if (!isNaN(d.getTime())) {
                const shouldTruncate = col.truncate !== false;
                return (
                  <span
                    className={`text-cf-textSecondary text-xs tabular-nums whitespace-nowrap block font-sans ${
                      shouldTruncate ? 'truncate' : 'overflow-visible'
                    }`}
                    title={d.toISOString()}
                  >
                    {d.toISOString().replace('T', ' ').slice(0, 19)}
                  </span>
                );
              }
            } catch {}
          }

          if (col.dataType === 'email') {
            const shouldTruncate = col.truncate !== false;
            return (
              <span
                className={`text-cf-textPrimary block whitespace-nowrap text-xs font-sans ${
                  shouldTruncate ? 'truncate' : 'overflow-visible'
                }`}
                title={String(val)}
              >
                {String(val)}
              </span>
            );
          }

          if (col.dataType === 'file' || (val && typeof val === 'object' && ('file_id' in val || 'filename' in val))) {
            const files = Array.isArray(val) ? val : [val];
            return (
              <div className="flex items-center gap-1.5 overflow-hidden">
                {files.map((f, idx) => {
                  const fname = typeof f === 'object' && f?.filename ? f.filename : String(f);
                  const { previewUrl } = resolveFileGatewayUrl(f);
                  return (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-xs text-amber-600 dark:text-amber-400 max-w-[180px] truncate font-sans"
                      title={fname}
                    >
                      <Paperclip size={11} className="shrink-0 text-amber-500" />
                      {previewUrl && previewUrl !== '#' ? (
                        <a
                          href={previewUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate hover:underline font-sans"
                          onClick={(e) => e.stopPropagation()}
                          title="Open verified file preview"
                        >
                          {fname}
                        </a>
                      ) : (
                        <span className="truncate font-sans">{fname}</span>
                      )}
                    </span>
                  );
                })}
              </div>
            );
          }

          // Nested objects and arrays
          if (typeof val === 'object') {
            return (
              <NestedObjectCell
                value={val}
                dataType={col.dataType}
                columnName={typeof col.header === 'string' ? col.header : String(col.id)}
                subfields={col.subfields}
              />
            );
          }

          // Invariant 15: Natural full-length ID rendering & standard cell rendering with font-sans
          const strVal = String(val);
          const shouldTruncate = col.truncate !== false;
          return (
            <span
              className={`block whitespace-nowrap font-sans text-xs text-cf-textPrimary ${
                shouldTruncate ? 'truncate' : 'overflow-visible'
              }`}
              title={strVal}
            >
              {strVal}
            </span>
          );
        },
      });
    });

    return list;
  }, [columns, enableSelection, canEdit, getRowId]);

  const visibilityColumnsList = useMemo(() => {
    return columns.map((c) => ({
      key: c.id,
      name: typeof c.header === 'string' ? c.header : c.id,
      isSystem: c.isSystem || c.pinned !== undefined,
    }));
  }, [columns]);

  return {
    tanstackColumns,
    visibilityColumnsList,
  };
}
